import { useQuery } from "@apollo/client";
import { Big } from "big.js";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { flexpa__FlexpaImportStatus, type GetFlaggedEobsQuery, gql, ingestion__IngestionStatus } from "@medbillai/generated-graphql-types";
import { errorHandling } from "../../../lib/apollo/utils";
import { computeWebAgentLinkStatusFromIngestions, FLEXPA_LINK_WAIT_TIME_MINUTES, WEB_AGENT_INGESTION_WAIT_TIME_MINUTES } from "../../accounts-link/utils/utils";
import { normalizeHealthDataEobs, normalizeHealthDataInvoices } from "./healthDataBill";
import type { NormalizedEob } from "./types";
import { parsePeriod } from "./utils";

// TODO: Move into its own useInsuranceLinkStatus file
const getInsuranceLinkStatus = gql(/* GraphQL */`
  query GetInsuranceLinkStatusQuery {
    me {
      id
      flexpaLinks {
        id
        active
        lastSyncedAt
        createdAt
        endpoint {
          displayName
          slug
        }
        imports(order: { startedAt: { dir: DESC } }, first: 1) {
          id
          status
        }
      }
      webAgentLinks {
        id
        active
        createdAt
        endpoint {
          type
          displayName
          slug
        }
        # TODO: Revert back to latestIngestion here whenever we trust that -
        # currently, it can include multiple ingestions per link, with no
        # guarantee which data belongs to which ingestion. This can lead to us
        # saying the import is still "in progress" even when the 
        latestExecution {
          ingestions {
            status
          }
        }
      }
      explanationOfBenefits {
        id
      }
    }
  }
`);

/*
  Ingestion health-data read path. Returns the user's EOB projection rows
  verbatim; `data` is a `v2.EOB` blob parsed client-side via
  `normalizeHealthDataEobs`. The list has no server-side pagination; tab
  filters run client-side.
*/
const getUserEobsQuery = gql(/* GraphQL */`
  query GetUserEobs {
    queryUserEobs {
      key
      kind
      data
      enrichment
      updatedAt
    }
  }
`);

/*
  Invoice projection rows; `data` is a `v2.Invoice` blob parsed client-side
  via `normalizeHealthDataInvoices`. Merged with EOB rows for the unified list.
*/
const getUserInvoicesQuery = gql(/* GraphQL */`
  query GetUserInvoices {
    queryUserInvoices {
      key
      kind
      data
      enrichment
      updatedAt
    }
  }
`);
export enum LoadingStatus {
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  IN_PROGRESS = "IN_PROGRESS",
}
interface InsuranceLink {
  linkId: string;
  displayName: string | undefined;
  slug: string | undefined;
  status: LoadingStatus;
}
export type InsuranceLinks = InsuranceLink[];
type Filter = {
  inNetwork?: boolean;
  outOfNetwork?: boolean;
  denied?: boolean;
  first?: number;
  after?: number;
};

/** Newest first; rows without a service date sort to the bottom. */
const byServiceDateDesc = (a: NormalizedEob, b: NormalizedEob): number => {
  const aTime = a.serviceDate ? a.serviceDate.getTime() : 0;
  const bTime = b.serviceDate ? b.serviceDate.getTime() : 0;
  return bTime - aTime;
};
export function useNormalizedEobs(filter?: Filter) {
  const eobs = useQuery(getUserEobsQuery, {
    context: errorHandling("caller"),
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network"
  });
  const invoices = useQuery(getUserInvoicesQuery, {
    context: errorHandling("caller"),
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network"
  });
  const healthDataNormalized = useMemo(() => {
    const normalizedEobs = normalizeHealthDataEobs(eobs.data?.queryUserEobs, {
      inNetwork: filter?.inNetwork,
      outOfNetwork: filter?.outOfNetwork,
      denied: filter?.denied
    });
    // Invoices have no decision/network, so they appear only under "All".
    const hasFilter = filter?.inNetwork || filter?.outOfNetwork || filter?.denied;
    const normalizedInvoices = hasFilter ? [] : normalizeHealthDataInvoices(invoices.data?.queryUserInvoices);
    return [...normalizedEobs, ...normalizedInvoices].sort(byServiceDateDesc);
  }, [eobs.data, invoices.data, filter?.inNetwork, filter?.outOfNetwork, filter?.denied]);
  const refetch = useCallback(async () => {
    const [eobResult] = await Promise.all([eobs.refetch(), invoices.refetch()]);
    return eobResult;
  }, [eobs.refetch, invoices.refetch]);
  return {
    networkStatus: eobs.networkStatus,
    loading: eobs.loading || invoices.loading,
    data: healthDataNormalized,
    error: eobs.error ?? invoices.error,
    refetch
  };
}

/**
 * Normalizes the insurance links for the bill screen empty state.
 */
export function useInsuranceLinkStatus() {
  const {
    data: dataLinks,
    loading: loadingLinks,
    refetch
  } = useQuery(getInsuranceLinkStatus, {
    context: errorHandling("caller")
  });
  const insuranceLinks: InsuranceLinks = useMemo(() => {
    // Logic for status mirrors getFlexpaLinkStatus && getWebLinkStatus
    // do not update unless you update those functions
    const flexpaLinks: InsuranceLink[] = dataLinks?.me?.flexpaLinks?.filter(link => link.active).map(link => {
      let status = link.imports?.[0]?.status;
      if (!link?.lastSyncedAt) {
        if (dayjs().diff(link.createdAt, "minutes") < FLEXPA_LINK_WAIT_TIME_MINUTES) {
          status = flexpa__FlexpaImportStatus.IN_PROGRESS;
        } else {
          status = flexpa__FlexpaImportStatus.FAILED;
        }
      }
      return {
        linkId: link.id,
        displayName: link.endpoint.displayName ?? undefined,
        slug: link.endpoint.slug ?? undefined,
        status: status as unknown as LoadingStatus
      };
    }) || [];
    const webAgentLinks: InsuranceLink[] = dataLinks?.me?.webAgentLinks?.filter(link => link.active).map(link => {
      let status = computeWebAgentLinkStatusFromIngestions(link.latestExecution?.ingestions);
      if (!status) {
        if (dayjs().diff(link.createdAt, "minutes") > WEB_AGENT_INGESTION_WAIT_TIME_MINUTES) {
          status = ingestion__IngestionStatus.FAILED;
        } else {
          status = ingestion__IngestionStatus.IN_PROGRESS;
        }
      }
      return {
        linkId: link.id,
        displayName: link.endpoint.displayName ?? undefined,
        slug: link.endpoint.slug ?? undefined,
        status: status as unknown as LoadingStatus
      };
    }) || [];
    return [...flexpaLinks, ...webAgentLinks];
  }, [dataLinks?.me?.flexpaLinks, dataLinks?.me?.webAgentLinks]);
  return {
    loading: loadingLinks,
    dataInsuranceLinks: insuranceLinks,
    refetch
  };
}

/**
 * Normalizes the raw EOB data for use in bill screens.
 */
export function normalizeEobData(eobs: GetFlaggedEobsQuery["queryFlaggedExplanationOfBenefits"]): NormalizedEob[] {
  if (!eobs) {
    return [];
  }
  return eobs.map(eob => {
    const memberLiability = eob.totalsByType?.memberLiability ?? eob.total?.find(t => t.category?.coding?.[0]?.code === "memberliability")?.amount;
    const totalDescription = eob.item?.reduce((acc, item) => {
      if (item?.productOrService?.text) {
        return `${acc + item.productOrService.text}, `;
      }
      return acc;
    }, "");
    const billablePeriod = parsePeriod(eob.billablePeriod);
    const serviceDate = eob.serviceDate || billablePeriod?.lower || eob.created;
    const nmData: NormalizedEob = {
      id: eob.id,
      resourceId: eob.resourceId,
      name: eob.patient?.display || null,
      status: eob.status,
      decision: eob.decision?.coding?.[0]?.code || null,
      totalCost: memberLiability ? {
        amount: Big(memberLiability.value ?? "0"),
        currency: memberLiability.currency ?? "USD"
      } : undefined,
      location: eob.provider?.display || eob.facility?.display || undefined,
      title: eob.title || undefined,
      serviceDate: serviceDate ? dayjs(serviceDate).toDate() : undefined,
      description: totalDescription || "No summary available"
    };
    return nmData;
  });
}